import React from 'react';
import styled from 'styled-components';
import { IconButton } from '@mui/material';
import { ChevronUp, ChevronDown } from 'react-feather';
import { colors } from '../../resources/theme/colors';
import { sa, DataTable } from '../../components/superAdminUi';

const { mandy, pictonBlue, redColor, purple, outrageousOrange, darkYellow } =
  colors;

// Status pill on the new Super Admin surface. Colours stay tied to the delivery status they
// have always signalled; only the shell (font, radius) moves onto the design system.
const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 8px;
  border-radius: ${sa.radius.sm};
  font-family: ${sa.font.family};
  font-size: 12px;
  font-weight: ${sa.font.bold};
  color: ${sa.color.white};
  background: ${(p) => p.$bg};
`;

const StatusCell = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ExpandPanel = styled.div`
  border-left: 3px solid ${sa.color.purple};
  background: #fafbfc;
  padding: 16px 20px;
`;

const STATUS_STYLE = {
  Sent: { label: 'Sent', bg: mandy },
  'Not Sent': { label: 'Waiting', bg: redColor },
  delivered: { label: 'Delivered', bg: pictonBlue },
  open: { label: 'Opened', bg: darkYellow },
  clicked: { label: 'Clicked', bg: outrageousOrange },
  bounce: { label: 'Bounced', bg: purple },
  processed: { label: 'Processed', bg: 'teal' },
};

export default function EmailList({ emails }) {
  const [showId, setShowId] = React.useState(null);

  const statusBadge = (status) => {
    const style = STATUS_STYLE[status] || { label: status, bg: purple };
    return <StatusBadge $bg={style.bg}>{style.label}</StatusBadge>;
  };

  const getStatusText = (logs) => {
    return logs.filter((x) => x.status === 'clicked').length > 0
      ? 'clicked'
      : logs.filter((x) => x.status === 'open').length > 0
      ? 'open'
      : logs.filter((x) => x.status === 'delivered').length > 0
      ? 'delivered'
      : logs.filter((x) => x.status === 'bounce').length > 0
      ? 'bounce'
      : logs.filter((x) => x.status === 'processed').length > 0
      ? 'processed'
      : '';
  };

  const columns = [
    { key: 'id', label: 'ID', width: '90px', render: (item) => item.id },
    {
      key: 'date',
      label: 'Date',
      width: '180px',
      render: (item) => new Date(Date.parse(item.createdAt)).toDateString(),
    },
    { key: 'subject', label: 'Subject', render: (item) => item.subject },
    { key: 'to', label: 'To', render: (item) => item.email },
    {
      key: 'action',
      label: 'Action',
      width: '220px',
      render: (item) => {
        const status =
          item.emailLogStatus.length === 0
            ? item.isSent
              ? 'Sent'
              : 'Not Sent'
            : getStatusText(item.emailLogStatus);
        return (
          <StatusCell>
            {statusBadge(status)}
            {item.emailLogStatus.length > 0 && (
              <IconButton
                disableRipple
                sx={{ padding: '0 !important' }}
                onClick={() =>
                  setShowId(showId === item.id ? null : item.id)
                }
              >
                {showId === item.id ? <ChevronUp /> : <ChevronDown />}
              </IconButton>
            )}
          </StatusCell>
        );
      },
    },
  ];

  const timelineColumns = [
    {
      key: 'timestamp',
      label: 'Timestamp',
      render: (log) =>
        new Date(log.updatedAt).toLocaleString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        }),
    },
    { key: 'status', label: 'Status', render: (log) => statusBadge(log.status) },
  ];

  return (
    <DataTable
      columns={columns}
      rows={emails || []}
      getRowKey={(item) => item.id}
      empty='No emails found. Please change filters.'
      isRowExpanded={(item) =>
        item.id === showId && item.emailLogStatus.length > 0
      }
      renderExpanded={(item) => (
        <ExpandPanel>
          <DataTable
            columns={timelineColumns}
            rows={item.emailLogStatus}
            getRowKey={(log, index) => index}
            size='sm'
          />
        </ExpandPanel>
      )}
    />
  );
}
